import { useCallback, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatError } from "helpers/formatError";

// Compact icon-only action that triggers a manual Meta sync, sized to sit
// inline with a sidebar group title. Owns its own in-flight state for the
// trigger request and toasts; the `isSyncing` prop keeps the spinner running
// until the backend `pending` status clears. The last-synced time is shown
// separately by MetaLastSynced.
const MetaSyncButton = ({
  onSync,
  onSynced,
  isSyncing = false,
  disabled = false,
  className,
}) => {
  const [isTriggering, setIsTriggering] = useState(false);
  const busy = isTriggering || isSyncing;

  const handleSync = useCallback(async () => {
    setIsTriggering(true);
    try {
      await onSync();
      toast.success("Sync started. Updated data will appear shortly.");
      onSynced?.();
    } catch (error) {
      toast.error(formatError(error, "Could not start sync"));
    } finally {
      setIsTriggering(false);
    }
  }, [onSync, onSynced]);

  return (
    <Tooltip>
      <TooltipTrigger render={<span className="tw:flex" />}>
        <Button
          variant="ghost"
          size="icon-sm"
          className={className}
          onClick={handleSync}
          disabled={busy || disabled}
          aria-label="Sync"
        >
          <RefreshCw className={cn(busy && "tw:animate-spin")} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">
        {disabled ? "Syncing is disabled for this account" : "Sync"}
      </TooltipContent>
    </Tooltip>
  );
};

export default MetaSyncButton;
